import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { usePrivy, useLogin } from "@privy-io/react-auth";

import Head from "next/head";
import Image from "next/image";

import { Game, games } from "../data/games";
import { NotificationProvider } from "../components/notification-provider";

export default function HomePage() {
  const router = useRouter();
  const { authenticated } = usePrivy();

  const [recentGames, setRecentGames] = useState<Game[]>([]);
  const [popularGames, setPopularGames] = useState<Game[]>([]);

  // supoort google wallet email login
  const { login } = useLogin({
    onComplete: () => {
      console.log('Login complete');
    },
    onError: (error) => {
      console.error('Login error:', error);
    }
  });

  // 从localStorage加载最近游戏
  useEffect(() => {
    // Popular games (only enabled games)
    loadRecentGames();
    setPopularGames(games.filter(game => game.enable !== false));
  }, []);

  const loadRecentGames = () => {
    const recentGameIds = JSON.parse(localStorage.getItem('recent-games') || '[]');
    const recentGames = recentGameIds
      .map((id: string) => games.find((game: Game) => game.id === id))
      .filter((game: Game | undefined): game is Game => game !== undefined && game.enable !== false);
    setRecentGames(recentGames);
  };

  // 添加游戏到最近游戏列表
  const addToRecentGames = (gameId: string) => {
    try {
      const recentGameIds = JSON.parse(localStorage.getItem('recent-games') || '[]');

      // 移除已存在的相同游戏ID（如果有的话）
      const filteredIds = recentGameIds.filter((id: string) => id !== gameId);

      // 将新游戏ID添加到开头
      const newRecentIds = [gameId, ...filteredIds].slice(0, 2); // 只保留最近2个

      // 保存到localStorage
      localStorage.setItem('recent-games', JSON.stringify(newRecentIds));

      // 更新状态
      const newRecentGames = newRecentIds
        .map(id => games.find((game: Game) => game.id === id))
        .filter((game: Game | undefined): game is Game => game !== undefined && game.enable !== false);
      setRecentGames(newRecentGames);
    } catch (error) {
      console.error('Error saving recent game:', error);
    }
  };

  const handleGameClick = (game: Game) => {
    if (!authenticated) {
      login();
      return;
    }

    // 添加到最近游戏列表
    setTimeout(() => {
      addToRecentGames(game.id);
    }, 500);

    router.push(`/games/${game.id}`);
  };

  const handleAuthAction = () => {
    if (authenticated) {
      router.push('/profile');
    } else {
      login();
    }
  };

  return (
    <NotificationProvider>
      <Head>
        <title>Game Platform - Discover Amazing Games</title>
        <meta name="description" content="Explore the hottest casual games" />
      </Head>

      <main className="min-h-screen">
        <div className="mobile-container bg-black rounded-md shadow-xl my-1">
          {/* Header */}
          <header className="flex items-center justify-between py-4 px-2">
            <div className="flex items-center">
              <div className="w-[40px] h-8 relative bg-transparent">
                <Image
                  src="/images/logo-white.png"
                  alt="Logo"
                  fill
                  className="object-contain"
                  priority
                  sizes="40px"
                  placeholder="empty"
                />
              </div>
            </div>
            <button
              onClick={handleAuthAction}
              className="flex items-center gap-2 bg-game-accent hover:bg-blue-600 text-white px-4 py-1 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {authenticated ? 'Profile' : 'Login'}
            </button>
          </header>

          <div className="px-4 pb-4">
            {/* Clickable Banner */}
            <section className="mb-8">
              {(() => {
                // 专门使用ID为 "dd" 的游戏作为banner
                const bannerGame = games.find(game => game.id === 'dd');
                
                // 如果找不到ID为 "dd" 的游戏，或者该游戏被禁用，不显示banner
                if (!bannerGame || bannerGame.enable === false) return null;
                
                return (
                  <div
                    onClick={() => handleGameClick(bannerGame)}
                    className="relative w-full rounded-2xl overflow-hidden cursor-pointer border border-game-card"
                  >
                    <Image
                      src={bannerGame.image}
                      alt={`${bannerGame.title} banner`}
                      width={0}
                      height={0}
                      className="w-full h-auto object-cover select-none"
                      priority
                      sizes="100vw"
                    />
                  </div>
                );
              })()}
            </section>

            {/* Recently Played - 只在有数据时显示 */}
            {recentGames.length > 0 && (
              <section className="mb-8">
                <h2 className="text-game-text text-lg font-semibold mb-4">Recently Played</h2>
                <div className="grid grid-cols-2 gap-2">
                  {recentGames.map((game) => (
                    <GameCard
                      key={game.id}
                      game={game}
                      onClick={() => handleGameClick(game)}
                      showCategory={false}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Popular Games */}
            <section>
              <h2 className="text-game-text text-lg font-semibold mb-4">Popular Games</h2>
              <div className="grid grid-cols-2 gap-2">
                {popularGames.map((game) => (
                  <GameCard
                    key={game.id}
                    game={game}
                    onClick={() => handleGameClick(game)}
                  />
                ))}
              </div>
            </section>
            <div className="mt-6 text-center text-game-text-secondary text-sm">
              No more games
            </div>
          </div>

          {/* Bottom Navigation hidden as requested */}
        </div>
      </main>
    </NotificationProvider>
  );
}

interface GameCardProps {
  game: Game;
  onClick: () => void;
  showCategory?: boolean;
}

function GameCard({ game, onClick, showCategory = true }: GameCardProps) {
  return (
    <div onClick={onClick} className="game-card">
      <div className="game-image">
        <Image
          src={game.image}
          alt={game.title}
          fill
          className="object-cover select-none"
          sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
          draggable={false}
        />
      </div>

      <div className="game-info">
        <div className="title-row">
          <h3 className="game-title">{game.title}</h3>
          {game.isNew && (
            <div className="badge new">
              NEW
            </div>
          )}
          {game.isHot && (
            <div className="badge hot">
              HOT
            </div>
          )}
        </div>
        {showCategory && (
          <>
            <div className="divider"></div>
            <p className="game-category">{game.category}</p>
          </>
        )}
      </div>
    </div>
  );
}
